/** SUNTRONIX ONE — ONE DOCUMENT VAULT (Phase 1).
 *  Business / Personal / Family secure document management on top of the
 *  encrypted object-storage layer.  Upload, drag-drop, bulk, search,
 *  Document 360, versioning, expiry, favourites, secure share + email,
 *  audit — with hard privacy isolation between the three vaults.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FolderLock, Building2, User, Users, Upload, Search, Download, Eye,
  Printer, Share2, Star, History, Trash2, Pencil, ShieldAlert, Clock,
  FileText, Plus, X, RefreshCw, Lock, CheckCircle2, Link2, Mail, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const VAULT_META = {
  BUSINESS: { label: "Business", icon: Building2, tone: "text-sky-700", ring: "ring-sky-200", bg: "bg-sky-50" },
  PERSONAL: { label: "Personal", icon: User, tone: "text-violet-700", ring: "ring-violet-200", bg: "bg-violet-50" },
  FAMILY: { label: "Family", icon: Users, tone: "text-emerald-700", ring: "ring-emerald-200", bg: "bg-emerald-50" },
};
const STATUS_TONE = {
  ACTIVE: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  EXPIRING_SOON: "bg-amber-50 text-amber-700 ring-amber-200",
  EXPIRED: "bg-rose-50 text-rose-700 ring-rose-200",
  SUPERSEDED: "bg-slate-100 text-slate-500 ring-slate-200",
  INVALID: "bg-slate-100 text-slate-500 ring-slate-200",
  INACTIVE: "bg-slate-100 text-slate-500 ring-slate-200",
};
const SENS_TONE = {
  STANDARD: "bg-slate-50 text-slate-600 ring-slate-200",
  CONFIDENTIAL: "bg-sky-50 text-sky-700 ring-sky-200",
  RESTRICTED: "bg-violet-50 text-violet-700 ring-violet-200",
  HIGHLY_RESTRICTED: "bg-rose-50 text-rose-700 ring-rose-200",
};

function Chip({ tone, children, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${tone}`}>
      {children}
    </span>
  );
}

async function blobAction(id, kind) {
  const enc = encodeURIComponent(id);
  const { data } = await api.get(`/vault/documents/${enc}/${kind === "print" ? "preview" : kind}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  if (kind === "download") {
    const a = document.createElement("a");
    a.href = url; a.download = ""; document.body.appendChild(a); a.click(); a.remove();
  } else {
    const w = window.open(url, "_blank");
    if (kind === "print" && w) { w.addEventListener("load", () => w.print()); }
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function OverviewCards({ overview, vault, setParam }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="vault-overview">
      {["BUSINESS", "PERSONAL", "FAMILY"].map((v) => {
        const m = VAULT_META[v]; const Icon = m.icon;
        const info = overview?.vaults?.[v];
        const locked = info && info.authorised === false;
        const active = vault === v;
        return (
          <button key={v} data-testid={`vault-card-${v.toLowerCase()}`}
            onClick={() => setParam("vault", v)}
            className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${active ? `${m.bg} ${m.ring} ring-2 border-transparent` : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center gap-2 ${m.tone}`}>
                <Icon className="h-5 w-5" /><span className="font-semibold">{m.label} Vault</span>
              </div>
              {locked ? <Lock className="h-4 w-4 text-slate-400" /> : null}
            </div>
            {locked ? (
              <div className="mt-3 text-[12px] text-slate-400">Access not authorised</div>
            ) : (
              <div className="mt-3 flex items-end gap-4">
                <div><div className="text-2xl font-bold text-slate-900">{info?.total ?? "—"}</div><div className="text-[11px] text-slate-400 uppercase tracking-wide">Documents</div></div>
                {info?.expiring ? <div className="text-amber-600 text-[12px] font-medium">{info.expiring} expiring</div> : null}
                {info?.expired ? <div className="text-rose-600 text-[12px] font-medium">{info.expired} expired</div> : null}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({ q, setQ, category, setCategory, categories, favOnly, setFavOnly, loadDocs, setUploadOpen }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="vault-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, number, issuer, tag, year…" className="pl-9" />
      </div>
      <Select value={category || "ALL"} onValueChange={(v) => setCategory(v === "ALL" ? "" : v)}>
        <SelectTrigger className="w-[190px]" data-testid="vault-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All categories</SelectItem>
          {categories.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button variant={favOnly ? "default" : "outline"} onClick={() => setFavOnly((f) => !f)} data-testid="vault-fav-toggle">
        <Star className="h-4 w-4 mr-1" /> Favourites
      </Button>
      <Button variant="outline" onClick={loadDocs} data-testid="vault-refresh"><RefreshCw className="h-4 w-4" /></Button>
      <Button variant="create" className="bg-slate-900 hover:bg-slate-800" onClick={() => setUploadOpen(true)} data-testid="vault-upload-btn">
        <Upload className="h-4 w-4 mr-1" /> Upload
      </Button>
    </div>
  );
}

function DocRow({ d, myEmail, onOpen, onShare, onFav, onDelete }) {
  const isFav = myEmail && (d.favourited_by || []).map((x) => String(x).toLowerCase()).includes(myEmail);
  return (
    <div data-testid="vault-doc-row"
      className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 hover:shadow-sm transition-all">
      <div className="h-9 w-9 rounded-md bg-slate-100 grid place-items-center text-slate-500 shrink-0">
        <FileText className="h-5 w-5" />
      </div>
      <button className="flex-1 text-left min-w-0" onClick={() => onOpen(d)} data-testid="vault-doc-open">
        <div className="font-medium text-slate-900 truncate flex items-center gap-2">
          {d.name}
          {d.source === "KYC" && <span className="text-[9px] font-semibold text-sky-600 border border-sky-200 rounded px-1">KYC</span>}
        </div>
        <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
          <span>{(d.category || "").replaceAll("_", " ")}</span>
          {d.document_number_masked && <span>· No. {d.document_number_masked}</span>}
          {d.document_number && <span>· No. {d.document_number}</span>}
          {d.expiry_date && <span>· Exp {String(d.expiry_date).slice(0, 10)}</span>}
          {d.version && d.version !== "1.0" && <span>· v{d.version}</span>}
        </div>
      </button>
      <Chip tone={SENS_TONE[d.sensitivity] || SENS_TONE.STANDARD}>{(d.sensitivity || "").replace("HIGHLY_", "H.")}</Chip>
      <Chip tone={STATUS_TONE[d.status] || STATUS_TONE.ACTIVE} testid="vault-doc-status">{d.status}</Chip>
      <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
        <Button size="icon" variant="ghost" title="Preview" onClick={() => blobAction(d.id, "preview")} data-testid="vault-doc-preview"><Eye className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Download" onClick={() => blobAction(d.id, "download")} data-testid="vault-doc-download"><Download className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Print" onClick={() => blobAction(d.id, "print")} data-testid="vault-doc-print"><Printer className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Share" onClick={() => onShare(d)} data-testid="vault-doc-share"><Share2 className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Favourite" onClick={() => onFav(d)} data-testid="vault-doc-fav"><Star className={`h-4 w-4 ${isFav ? "fill-amber-400 text-amber-400" : ""}`} /></Button>
        {!d.read_only && <Button size="icon" variant="ghost" title="Delete" onClick={() => onDelete(d)} data-testid="vault-doc-delete"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
      </div>
    </div>
  );
}


export default function OneDocumentVault() {
  const [sp, setSp] = useSearchParams();
  const vault = (sp.get("vault") || "BUSINESS").toUpperCase();
  const tab = sp.get("tab") || "documents";
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [authorised, setAuthorised] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [shares, setShares] = useState([]);
  const [audit, setAudit] = useState([]);
  const { user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();

  const setParam = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/vault/meta"); setMeta(data); } catch { /* noop */ }
  }, []);
  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/vault/dashboard/overview"); setOverview(data); } catch { /* noop */ }
  }, []);
  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { vault, limit: 60 };
      if (q) params.q = q;
      if (category) params.category = category;
      if (favOnly) params.favourites = true;
      const { data } = await api.get("/vault/documents", { params });
      setDocs(data.items || []); setTotal(data.total || 0); setAuthorised(data.authorised !== false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [vault, q, category, favOnly]);

  useEffect(() => { loadMeta(); loadOverview(); }, [loadMeta, loadOverview]);
  useEffect(() => {
    if (sp.get("action") === "upload") {
      setUploadOpen(true);
      const n = new URLSearchParams(sp); n.delete("action"); setSp(n, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (tab === "documents") { const t = setTimeout(() => loadDocs(), 250); return () => clearTimeout(t); } }, [tab, loadDocs]);
  useEffect(() => {
    if (tab === "expiring") api.get("/vault/expiry").then(({ data }) => setExpiring(data.items || []));
    if (tab === "shared") api.get("/vault/shares").then(({ data }) => setShares(data.items || []));
    if (tab === "audit") api.get("/vault/audit").then(({ data }) => setAudit(data.items || []));
  }, [tab]);

  const categories = useMemo(() => {
    if (!meta) return [];
    return vault === "BUSINESS" ? meta.business_categories : meta.personal_categories;
  }, [meta, vault]);

  const toggleFav = async (d) => {
    try { await api.post(`/vault/documents/${encodeURIComponent(d.id)}/favourite`); loadDocs(); loadOverview(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doDelete = async (d) => {
    if (!window.confirm(`Mark "${d.name}" as INVALID? (safe delete — history preserved)`)) return;
    try { await api.delete(`/vault/documents/${encodeURIComponent(d.id)}?mode=MARK_INVALID`); toast.success("Marked invalid"); loadDocs(); loadOverview(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const m = VAULT_META[vault] || VAULT_META.BUSINESS;

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6" data-testid="one-document-vault">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 flex items-center gap-2">
            <FolderLock className="h-3.5 w-3.5" /> Documents
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ONE Document Vault</h1>
          <p className="text-[12px] text-slate-500 mt-1">Upload once · store safely · search instantly · share securely. Encrypted at rest & in transit — no public links.</p>
        </div>
      </div>

      <OverviewCards overview={overview} vault={vault} setParam={setParam} />

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["documents", "Documents"], ["expiring", "Expiring"], ["shared", "Shared"], ["audit", "Audit"]].map(([id, label]) => (
          <button key={id} onClick={() => setParam("tab", id === "documents" ? "" : id)}
            data-testid={`vault-tab-${id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        <div className="space-y-4">
          <div className={`flex items-center gap-2 text-sm font-semibold ${m.tone}`}>
            <m.icon className="h-4 w-4" /> {m.label} Vault
          </div>
          <Toolbar q={q} setQ={setQ} category={category} setCategory={setCategory}
            categories={categories} favOnly={favOnly} setFavOnly={setFavOnly}
            loadDocs={loadDocs} setUploadOpen={setUploadOpen} />
          {!authorised ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-10 text-center text-slate-400" data-testid="vault-unauthorised">
              <Lock className="h-8 w-8 mx-auto mb-2" />
              You are not authorised to view this vault.
            </div>
          ) : loading ? (
            <div className="text-slate-400 text-sm p-6">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="vault-empty">
              No documents yet. Click Upload to add your first document.
            </div>
          ) : (
            <div className="space-y-2" data-testid="vault-doc-list">
              <div className="text-[11px] text-slate-400">{total} document(s)</div>
              {docs.map((d) => <DocRow key={d.id} d={d} myEmail={myEmail}
                onOpen={setDetail} onShare={setShareDoc} onFav={toggleFav} onDelete={doDelete} />)}
            </div>
          )}
        </div>
      )}

      {tab === "expiring" && (
        <div className="space-y-2" data-testid="vault-expiring-list">
          {expiring.length === 0 ? <div className="text-slate-400 text-sm p-6">Nothing expiring soon.</div>
            : expiring.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <Clock className={`h-4 w-4 ${d.status === "EXPIRED" ? "text-rose-500" : "text-amber-500"}`} />
                <button className="flex-1 text-left" onClick={() => setDetail(d)}>
                  <div className="font-medium text-slate-900">{d.name}</div>
                  <div className="text-[11px] text-slate-400">{d.vault_type} · Exp {String(d.expiry_date).slice(0, 10)}</div>
                </button>
                <Chip tone={STATUS_TONE[d.status]}>{d.status}</Chip>
              </div>
            ))}
        </div>
      )}

      {tab === "shared" && (
        <div className="space-y-2" data-testid="vault-shared-list">
          {shares.length === 0 ? <div className="text-slate-400 text-sm p-6">No active shares.</div>
            : shares.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <Link2 className="h-4 w-4 text-slate-400" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">{s.doc_name}</div>
                  <div className="text-[11px] text-slate-400">{s.method} · to {s.recipient_email || "link"} · exp {String(s.expiry).slice(0, 16).replace("T", " ")} · {s.download_count} downloads {s.revoked ? "· REVOKED" : ""}</div>
                </div>
                {!s.revoked && <Button size="sm" variant="outline" onClick={async () => { await api.post(`/vault/shares/${s.id}/revoke`); toast.success("Revoked"); api.get("/vault/shares").then(({ data }) => setShares(data.items || [])); }} data-testid="vault-revoke-share">Revoke</Button>}
              </div>
            ))}
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-1" data-testid="vault-audit-list">
          {audit.length === 0 ? <div className="text-slate-400 text-sm p-6">No audit events.</div>
            : audit.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-[12px] border-b border-slate-100 py-2">
                <span className="font-mono text-slate-400 w-36 shrink-0">{String(a.at).slice(0, 16).replace("T", " ")}</span>
                <span className="font-semibold text-slate-700 w-40 shrink-0">{a.action}</span>
                <span className="text-slate-500 truncate">{a.actor} {a.extra?.name ? `· ${a.extra.name}` : ""}</span>
              </div>
            ))}
        </div>
      )}

      {uploadOpen && <UploadDialog meta={meta} vault={vault} onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); loadDocs(); loadOverview(); }} />}
      {detail && <DetailDrawer doc={detail} onClose={() => setDetail(null)} onChanged={() => { loadDocs(); loadOverview(); }} onShare={(d) => { setDetail(null); setShareDoc(d); }} />}
      {shareDoc && <ShareDialog doc={shareDoc} onClose={() => setShareDoc(null)} />}
    </div>
  );
}

/* ── Upload dialog with drag & drop ─────────────────────────────────────── */
function UploadDialog({ meta, vault, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", vault_type: vault, category: "", sensitivity: "STANDARD",
    document_number: "", issuer: "", issue_date: "", expiry_date: "",
    country: "India", fy: "", tags: "", notes: "", owner_email: "", family_member: "",
  });
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cats = form.vault_type === "BUSINESS" ? (meta?.business_categories || []) : (meta?.personal_categories || []);

  const pick = (f) => { if (!f) return; setFile(f); if (!form.name) set("name", f.name.replace(/\.[^.]+$/, "")); };

  const submit = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    if (!form.name || !form.category) { toast.error("Name and category are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append("file", file);
      const { data } = await api.post("/vault/documents", fd);
      if (data.possible_duplicate) toast.warning(`Possible duplicate of "${data.possible_duplicate.name}" — saved as new anyway.`);
      else toast.success("Document uploaded");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vault-upload-dialog">
        <DialogHeader><DialogTitle>Upload document</DialogTitle>
          <DialogDescription>Stored encrypted. Confirm the classification below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => fileRef.current?.click()}
            data-testid="vault-dropzone"
            className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-slate-900 bg-slate-50" : "border-slate-300"}`}>
            <Upload className="h-6 w-6 mx-auto text-slate-400 mb-2" />
            {file ? <div className="text-sm font-medium text-slate-800">{file.name}</div>
              : <div className="text-sm text-slate-500">Drag & drop, tap to browse, or use camera on mobile</div>}
            <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.ppt,.pptx" capture="environment"
              onChange={(e) => pick(e.target.files?.[0])} data-testid="vault-file-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Vault">
              <Select value={form.vault_type} onValueChange={(v) => { set("vault_type", v); set("category", ""); }}>
                <SelectTrigger data-testid="vault-upload-vault"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.vaults || ["BUSINESS", "PERSONAL", "FAMILY"]).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category *">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="vault-upload-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-64">{cats.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Document name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="vault-upload-name" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Sensitivity">
              <Select value={form.sensitivity} onValueChange={(v) => set("sensitivity", v)}>
                <SelectTrigger data-testid="vault-upload-sensitivity"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.sensitivities || []).map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Expiry date"><Input type="date" value={form.expiry_date?.slice(0, 10) || ""} onChange={(e) => set("expiry_date", e.target.value)} data-testid="vault-upload-expiry" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Document number"><Input value={form.document_number} onChange={(e) => set("document_number", e.target.value)} /></Field>
            <Field label="Issuer"><Input value={form.issuer} onChange={(e) => set("issuer", e.target.value)} /></Field>
          </div>
          {form.vault_type === "FAMILY" && (
            <Field label="Family member / household"><Input value={form.family_member} onChange={(e) => set("family_member", e.target.value)} placeholder="e.g. Household, or a member name" /></Field>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Tags (comma separated)"><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="CHENNAI PROPERTY, FY 2025-26" /></Field>
            <Field label="Financial year"><Input value={form.fy} onChange={(e) => set("fy", e.target.value)} placeholder="FY 2025-26" /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="create" className="bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={busy} data-testid="vault-upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (<label className="block"><div className="text-[11px] font-medium text-slate-500 mb-1">{label}</div>{children}</label>);
}

/* ── Document 360 drawer ────────────────────────────────────────────────── */
function DetailDrawer({ doc, onClose, onChanged, onShare }) {
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    api.get(`/vault/documents/${encodeURIComponent(doc.id)}`).then(({ data }) => { setFull(data); setForm(data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"));
  }, [doc.id]);
  const d = full || doc;
  const saveEdit = async () => {
    try {
      const body = { name: form.name, category: form.category, sensitivity: form.sensitivity,
        document_number: form.document_number, issuer: form.issuer, expiry_date: form.expiry_date,
        notes: form.notes, tags: (form.tags || []).join ? form.tags : form.tags };
      await api.patch(`/vault/documents/${encodeURIComponent(doc.id)}`, body);
      toast.success("Saved"); setEditing(false); onChanged();
      const { data } = await api.get(`/vault/documents/${encodeURIComponent(doc.id)}`); setFull(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vault-detail-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">{d.name}
            {d.source === "KYC" && <span className="text-[9px] font-semibold text-sky-600 border border-sky-200 rounded px-1">KYC</span>}
          </DialogTitle>
          <DialogDescription>{(d.category || "").replaceAll("_", " ")} · {d.vault_type} vault</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => blobAction(d.id, "preview")}><Eye className="h-4 w-4 mr-1" />Preview</Button>
          {d.can_download !== false && <Button size="sm" variant="outline" onClick={() => blobAction(d.id, "download")}><Download className="h-4 w-4 mr-1" />Download</Button>}
          <Button size="sm" variant="outline" onClick={() => blobAction(d.id, "print")}><Printer className="h-4 w-4 mr-1" />Print</Button>
          <Button size="sm" variant="outline" onClick={() => onShare(d)}><Share2 className="h-4 w-4 mr-1" />Share</Button>
          {!d.read_only && !editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="vault-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
        </div>
        {editing ? (
          <div className="space-y-2 mt-2">
            <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="vault-edit-name" /></Field>
            <Field label="Document number"><Input value={form.document_number || ""} onChange={(e) => setForm((f) => ({ ...f, document_number: e.target.value }))} /></Field>
            <Field label="Expiry date"><Input type="date" value={(form.expiry_date || "").slice(0, 10)} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} /></Field>
            <Field label="Notes"><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
            <div className="flex gap-2"><Button size="sm" onClick={saveEdit} data-testid="vault-edit-save">Save</Button><Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button></div>
          </div>
        ) : (
          <dl className="mt-2 grid grid-cols-3 gap-y-2 text-sm">
            <Detail k="Status" v={d.status} />
            <Detail k="Sensitivity" v={d.sensitivity} />
            <Detail k="Version" v={d.version} />
            <Detail k="Number" v={d.document_number || d.document_number_masked || "—"} />
            <Detail k="Issuer" v={d.issuer || "—"} />
            <Detail k="Expiry" v={d.expiry_date ? String(d.expiry_date).slice(0, 10) : "—"} />
            <Detail k="Owner" v={d.owner_email || "—"} />
            <Detail k="Uploaded" v={d.uploaded_at ? String(d.uploaded_at).slice(0, 10) : "—"} />
            <Detail k="Size" v={d.size ? `${Math.round(d.size / 1024)} KB` : "—"} />
          </dl>
        )}
        {full?.versions?.length > 1 && (
          <div className="mt-3">
            <div className="text-[11px] font-semibold text-slate-500 flex items-center gap-1"><History className="h-3.5 w-3.5" />Version history</div>
            {full.versions.map((v) => <div key={v.id} className="text-[12px] text-slate-500 py-1">v{v.version} · {v.status} · {String(v.uploaded_at).slice(0, 10)}</div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
function Detail({ k, v }) {
  return (<div className="col-span-1"><dt className="text-[10px] uppercase tracking-wide text-slate-400">{k}</dt><dd className="text-slate-800 font-medium truncate">{v}</dd></div>);
}

/* ── Share dialog (secure link + email) ─────────────────────────────────── */
function ShareDialog({ doc, onClose }) {
  const [mode, setMode] = useState("link");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [form, setForm] = useState({ recipient_email: "", expiry_hours: 24, password: "", max_downloads: "", download_allowed: true, message: "", as_attachment: false, confirm_sensitive: false });
  const sensitive = ["RESTRICTED", "HIGHLY_RESTRICTED"].includes(doc.sensitivity);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vault/documents/${encodeURIComponent(doc.id)}/share/link`, {
        recipient_email: form.recipient_email || null, expiry_hours: Number(form.expiry_hours) || 24,
        download_allowed: form.download_allowed, password: form.password || null,
        max_downloads: form.max_downloads ? Number(form.max_downloads) : null,
      });
      const full = `${window.location.origin}${data.url}`;
      setLink(full); toast.success("Secure link created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendEmail = async () => {
    if (!form.recipient_email) { toast.error("Recipient email required"); return; }
    if (sensitive && !form.confirm_sensitive) { toast.error("Please confirm sensitive document sharing"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/vault/documents/${encodeURIComponent(doc.id)}/share/email`, {
        recipient_email: form.recipient_email, message: form.message, as_attachment: form.as_attachment,
        expiry_hours: Number(form.expiry_hours) || 48, confirm_sensitive: form.confirm_sensitive,
      });
      if (data.ok) { toast.success(`Sent via ${data.method}`); onClose(); }
      else { toast.error(`Delivery failed (${data.delivery_status}). Check the recipient address.`); }
    } catch (e) {
      if (e.response?.status === 428) toast.error("Please confirm sensitive document sharing");
      else toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vault-share-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="h-4 w-4" />Share “{doc.name}”</DialogTitle>
          <DialogDescription>{sensitive ? "This is a sensitive document — secure link recommended." : "Choose how to share."}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 mb-2">
          <Button size="sm" variant={mode === "link" ? "default" : "outline"} onClick={() => setMode("link")} data-testid="share-mode-link"><Link2 className="h-4 w-4 mr-1" />Secure link</Button>
          <Button size="sm" variant={mode === "email" ? "default" : "outline"} onClick={() => setMode("email")} data-testid="share-mode-email"><Mail className="h-4 w-4 mr-1" />Email</Button>
        </div>
        {sensitive && (
          <label className="flex items-center gap-2 text-[12px] text-rose-600 mb-1" data-testid="share-confirm-sensitive">
            <input type="checkbox" checked={form.confirm_sensitive} onChange={(e) => set("confirm_sensitive", e.target.checked)} />
            <ShieldAlert className="h-4 w-4" /> I confirm sharing this sensitive document
          </label>
        )}
        {mode === "link" ? (
          <div className="space-y-2">
            <Field label="Recipient email (optional)"><Input value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Expiry (hours)"><Input type="number" value={form.expiry_hours} onChange={(e) => set("expiry_hours", e.target.value)} data-testid="share-expiry" /></Field>
              <Field label="Max downloads (optional)"><Input type="number" value={form.max_downloads} onChange={(e) => set("max_downloads", e.target.value)} /></Field>
            </div>
            <Field label="Password (optional)"><Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="share-password" /></Field>
            <label className="flex items-center gap-2 text-[12px] text-slate-600"><Switch checked={form.download_allowed} onCheckedChange={(v) => set("download_allowed", v)} />Allow download</label>
            {link ? (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2 flex items-center gap-2" data-testid="share-link-result">
                <input readOnly value={link} className="flex-1 bg-transparent text-[11px] text-slate-600 outline-none" />
                <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
              </div>
            ) : (
              <Button variant="create" className="w-full bg-slate-900 hover:bg-slate-800" onClick={createLink} disabled={busy} data-testid="share-create-link">{busy ? "Creating…" : "Create secure link"}</Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Field label="Recipient email *"><Input value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} data-testid="share-email-recipient" /></Field>
            <Field label="Message"><Textarea rows={2} value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
            <label className="flex items-center gap-2 text-[12px] text-slate-600">
              <Switch checked={form.as_attachment} onCheckedChange={(v) => set("as_attachment", v)} disabled={doc.sensitivity === "HIGHLY_RESTRICTED"} />
              Send as attachment {doc.sensitivity === "HIGHLY_RESTRICTED" ? "(disabled — link only)" : "(otherwise secure link)"}
            </label>
            <Button variant="comms" className="w-full bg-slate-900 hover:bg-slate-800" onClick={sendEmail} disabled={busy} data-testid="share-send-email">{busy ? "Sending…" : "Send"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
